"use client";
import { useAtom } from "jotai";
import { FileText, Hash, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { MAX_RECENT_SUBTITLES, recentSubtitlesAtom, removeRecentSubtitle } from "./recentSubtitles";

interface RecentSubtitlesCardsProps {
  onSelect: (content: string) => void;
  disabled?: boolean;
}

export const RecentSubtitlesCards = ({ onSelect, disabled = false }: RecentSubtitlesCardsProps) => {
  const [recentSubtitles, setRecentSubtitles] = useAtom(recentSubtitlesAtom);

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - timestamp) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return "Just now";
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else if (diffInHours < 168) {
      // 7 days
      return `${Math.floor(diffInHours / 24)}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const handleRemove = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setRecentSubtitles(removeRecentSubtitle(id));
  };

  // Show only the most recent based on MAX_RECENT_SUBTITLES
  const recentSubtitlesToShow = recentSubtitles.slice(0, MAX_RECENT_SUBTITLES);

  if (recentSubtitlesToShow.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
        Recent Subtitles
      </label>
      <div className="grid grid-cols-1 gap-2">
        {recentSubtitlesToShow.map((subtitle) => (
          <div
            key={subtitle.id}
            onClick={() => !disabled && onSelect(subtitle.content)}
            className={cn(
              "p-3 border rounded-md cursor-pointer transition-colors hover:border-foreground/50 hover:bg-muted/50 group",
              disabled && "opacity-50 cursor-not-allowed"
            )}
          >
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center space-x-2 flex-1 min-w-0">
                <FileText className="h-3 w-3 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium truncate">{subtitle.name}</h4>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {subtitle.captionCount && (
                  <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                    <Hash className="h-3 w-3" />
                    <span>{subtitle.captionCount}</span>
                  </div>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => handleRemove(e, subtitle.id)}
                  className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>

            <div className="text-xs text-muted-foreground line-clamp-1">
              {subtitle.content.slice(0, 100)}
              {subtitle.content.length > 100 && "..."}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
